import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom, isAdminAtom, isMobileAtom, jobTypesAtom } from "../../../../../-Atoms";

// Firebase imports
import { db, updateDoc, doc } from "../../../../../firebaseConfig";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import RemoveIcon from "@mui/icons-material/Remove";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import UniversalTextField from "../../../../../GlobalComponents/UniversalTextField";

const BreakdownItemV2 = ({
  item,
  items,
  setItems,
  index,
  showHiddenItems,
  showRemoveBreakdownItem,
  jobData,
  imageBreakdownClient,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [isMobile] = useAtom(isMobileAtom);
  const [jobTypes] = useAtom(jobTypesAtom);

  // State for form fields
  const [description, setDescription] = useState("");
  const [jobTypesFilter, setJobTypesFilter] = useState([]);
  const [quantityProcessed, setQuantityProcessed] = useState("");
  const [filesInPost, setFilesInPost] = useState("");
  const [quantitySupplied, setQuantitySupplied] = useState("");
  const [expectedPerHour, setExpectedPerHour] = useState("");
  const [costPerFile, setCostPerFile] = useState("");
  const [existingItem, setExistingItem] = useState("");
  const [itemHidden, setItemHidden] = useState("");

  // Loading states for each field
  const [isTyping, setIsTyping] = useState({
    description: false,
    quantityProcessed: false,
    filesInPost: false,
    quantitySupplied: false,
    itemHidden: false,
  });

  // Queue system for managing multiple rapid changes
  const updateQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const latestItemsRef = useRef([]);
  const jobDataIdRef = useRef(jobData.id);

  // Keep track of the latest items state and job ID
  useEffect(() => {
    latestItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    jobDataIdRef.current = jobData.id;
  }, [jobData.id]);

  // Queue processor that handles updates in order
  const processUpdateQueue = useCallback(async () => {
    if (isProcessingRef.current || updateQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    try {
      // Get the most recent items state
      const currentItems = [...latestItemsRef.current];

      // Process all queued changes to get the final state
      const fieldsBeingUpdated = new Set();

      while (updateQueueRef.current.length > 0) {
        const { field } = updateQueueRef.current.shift();
        fieldsBeingUpdated.add(field);
      }

      // Perform single database update with latest state
      await updateDoc(doc(db, "JobData", jobDataIdRef.current), {
        imageBreakdown: currentItems
      });

      // Clear loading states for all fields that were updated
      setIsTyping((prev) => {
        const newState = { ...prev };
        fieldsBeingUpdated.forEach((field) => {
          newState[field] = false;
        });
        return newState;
      });
    } catch (error) {
      console.error("Failed to update:", error);
      // Clear all loading states on error
      setIsTyping({
        description: false,
        quantityProcessed: false,
        filesInPost: false,
        quantitySupplied: false,
        itemHidden: false,
      });
    } finally {
      isProcessingRef.current = false;

      // Check if more updates were queued while processing
      if (updateQueueRef.current.length > 0) {
        setTimeout(processUpdateQueue, 50); // Small delay before processing next batch
      }
    }
  }, []);


  // Generic function to queue field updates
  const queueFieldUpdate = useCallback(
    (field) => {
      setIsTyping((prev) => ({ ...prev, [field]: true }));

      updateQueueRef.current.push({
        field,
        timestamp: Date.now(),
      });

      processUpdateQueue();
    },
    [processUpdateQueue]
  );

  // Check if item is empty (no filled fields)
  const isItemEmpty = () => {
    return !item.description?.trim() && 
           !item.quantityProcessed?.toString().trim() && 
           !item.filesInPost?.toString().trim() && 
           !item.quantitySupplied?.toString().trim();
  };

  const removeBreakdownItem = async () => {
    // Only allow deletion of empty items
    if (!isItemEmpty()) {
      console.warn("Cannot delete breakdown item with filled fields");
      return;
    }

    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
    
    // Directly update Firestore with the new array instead of using the queue system
    try {
      await updateDoc(doc(db, "JobData", jobDataIdRef.current), {
        imageBreakdown: newItems
      });
    } catch (error) {
      console.error("Failed to delete breakdown item:", error);
      // Revert local state on error
      setItems(items);
    }
  };

  const [isMatching, setIsMatching] = useState(
    isJobTypeMatching(jobTypes, jobTypesFilter, jobData?.jobTypeFull)
  );

  // Handler functions for each field
  const handleDescriptionChange = (newValue) => {
    if (existingItem) return;
    setDescription(newValue);
    item.description = newValue;
    queueFieldUpdate("description");
  };

  const handleQuantityProcessedChange = (newValue) => {
    const numericValue = newValue.replace(/[^0-9]/g, "");
    setQuantityProcessed(numericValue);
    item.quantityProcessed = numericValue;
    queueFieldUpdate("quantityProcessed");
  };

  const handleFilesInPostChange = (newValue) => {
    const numericValue = newValue.replace(/[^0-9]/g, "");
    setFilesInPost(numericValue);
    item.filesInPost = numericValue;
    queueFieldUpdate("filesInPost");
  };

  const handleQuantitySuppliedChange = (newValue) => {
    const numericValue = newValue.replace(/[^0-9]/g, "");
    setQuantitySupplied(numericValue);
    item.quantitySupplied = numericValue;
    queueFieldUpdate("quantitySupplied");
  };

  const handleItemHiddenToggle = () => {
    const newHiddenState = !itemHidden;
    setItemHidden(newHiddenState);
    item.itemHidden = newHiddenState;
    const updatedItems = [...items];
    setItems(updatedItems);
    queueFieldUpdate("itemHidden");
  };

  useEffect(() => {
    setIsMatching(
      isJobTypeMatching(jobTypes, jobTypesFilter, jobData?.jobTypeFull)
    );
  }, [setIsMatching, jobTypes, jobTypesFilter, jobData]);

  useEffect(() => {
    if (!arrayHasId(imageBreakdownClient, item.id)) {
      if (quantityProcessed !== item.quantityProcessed) {
        setQuantityProcessed(item.quantityProcessed);
      }
      if (filesInPost !== item.filesInPost) {
        setFilesInPost(item.filesInPost);
      }
      if (quantitySupplied !== item.quantitySupplied) {
        setQuantitySupplied(item.quantitySupplied);
      }
      if (itemHidden !== item.itemHidden) {
        setItemHidden(item.itemHidden);
      }
      if (description !== item.description) {
        setDescription(item.description);
      }
      if (expectedPerHour !== item.expectedPerHour) {
        setExpectedPerHour(item.expectedPerHour);
      }
      if (costPerFile !== item.costPerFile) {
        setCostPerFile(item.costPerFile);
      }
      if (existingItem !== item.existingItem) {
        setExistingItem(item.existingItem);
      }
    } else {
      if (quantityProcessed !== item.quantityProcessed) {
        setQuantityProcessed(item.quantityProcessed);
      }
      if (filesInPost !== item.filesInPost) {
        setFilesInPost(item.filesInPost);
      }
      if (quantitySupplied !== item.quantitySupplied) {
        setQuantitySupplied(item.quantitySupplied);
      }
      if (itemHidden !== item.itemHidden) {
        setItemHidden(item.itemHidden);
      }
      setExistingItem(true);
    }
  }, [
    item,
    imageBreakdownClient,
    description,
    existingItem,
    costPerFile,
    expectedPerHour,
    itemHidden,
    quantitySupplied,
    filesInPost,
    quantityProcessed,
  ]);

  useEffect(() => {
    if (arrayHasId(imageBreakdownClient, item.id)) {
      const index = findIndexById(imageBreakdownClient, item.id);
      setDescription(imageBreakdownClient[index].description);
      setExpectedPerHour(imageBreakdownClient[index].expectedPerHour);
      setCostPerFile(imageBreakdownClient[index].costPerFile);
      setJobTypesFilter(imageBreakdownClient[index].jobTypesFilter);

      let push = false;
      if (item.description !== imageBreakdownClient[index].description) {
        item.description = imageBreakdownClient[index].description;
        push = true;
      }
      if (
        item.expectedPerHour !== imageBreakdownClient[index].expectedPerHour
      ) {
        item.expectedPerHour = imageBreakdownClient[index].expectedPerHour;
        push = true;
      }
      if (item.costPerFile !== imageBreakdownClient[index].costPerFile) {
        item.costPerFile = imageBreakdownClient[index].costPerFile;
        push = true;
      }
      try {
        if (
          !arraysAreEqual(
            item.jobTypesFilter,
            imageBreakdownClient[index].jobTypesFilter,
            imageBreakdownClient[index]
          )
        ) {
          item.jobTypesFilter = imageBreakdownClient[index].jobTypesFilter;
          push = true;
        }
      } catch (e) {
        item.jobTypesFilter = [];
        push = true;
      }
      if (push) {
        updateDoc(doc(db, "JobData", jobData.id), { imageBreakdown: items })
          .catch((e) => console.log(e));
      }
    }
  }, [imageBreakdownClient, item, items, jobData]);

  // Helper functions
  function arraysAreEqual(array1, array2, index) {
    if (array1.length !== array2.length) {
      return false;
    }
    for (let i = 0; i < array1.length; i++) {
      if (array1[i] !== array2[i]) {
        return false;
      }
    }
    return true;
  }

  function arrayHasId(array, id) {
    return array.some((item) => item.id === id);
  }

  function findIndexById(array, targetId) {
    for (let i = 0; i < array.length; i++) {
      if (array[i].id === targetId) {
        return i;
      }
    }
    return -1;
  }

  function isJobTypeMatching(jobTypes, noteJobTypes, jobType) {
    let res = false;
    if (!noteJobTypes) {
      return true;
    }
    if (noteJobTypes.length === 0) {
      return true;
    }

    jobTypes.forEach((jt) => {
      const combined = `${jt.name} (-${jt.nameShort})`;
      if (combined === jobType) {
        noteJobTypes.forEach((njt) => {
          if (jt.id === njt) res = true;
        });
      }
    });
    return res;
  }

  const isVideoJob = (jobData) => {
    if (jobData.jobTypeFull?.includes("-V")) return true;
    if (jobData.jobTypeFull?.includes("-E")) return true;
    return false;
  };

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] },
    },
  };

  if (!isMatching) return null;

  return (
    <motion.div variants={itemVariants}>
      <Box
        sx={{
          display: showHiddenItems ? "block" : itemHidden ? "none" : "block",
          borderRadius: "8px",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.4)" : "rgba(235,235,235,0.4)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          padding: 1.5,
          marginBottom: 1,
          opacity: itemHidden ? 0.4 : 1,
        }}
      >
        {/* Description field */}
        <Box sx={{ marginBottom: 1 }}>
          <UniversalTextField
            value={`${description}${
              isAdmin && costPerFile ? ` - $${costPerFile}++ Per File` : ""
            }`}
            placeholder="Replace Me - e.g Ghost / Life / Jackets / T-Shirts etc..."
            multiline
            hiddenLabel
            disabled={Boolean(existingItem)}
            onCallback={handleDescriptionChange}
            adminOnly={false}
            sx={{
              marginTop: 0,
              fontWeight: existingItem ? "bold" : "normal",
              "& .MuiInputBase-input": {
                color: theme === "dark" ? "#ffffff" : "#000000",
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiInputLabel-root": {
                color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiFormControl-root > .MuiBox-root": {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            }}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <>
                  {existingItem ? (
                    quantityProcessed === "" ? (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={isTyping.itemHidden}
                          onClick={handleItemHiddenToggle}
                        >
                          {!itemHidden ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  ) : showRemoveBreakdownItem && isItemEmpty() ? (
                    <InputAdornment position="end">
                      <IconButton onClick={removeBreakdownItem}>
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null}
                </>
              ),
            }}
          />
        </Box>

        {/* Quantity fields */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: isMobile ? "wrap" : "nowrap",
            alignItems: "flex-start",
          }}
        >
          <UniversalTextField
            value={quantityProcessed}
            label="Processed"
            disabled={itemHidden}
            onCallback={handleQuantityProcessedChange}
            adminOnly={false}
            sx={{
              marginTop: 0,
              width: isMobile ? "47%" : "100%",
              "& .MuiInputBase-input": {
                color: theme === "dark" ? "#ffffff" : "#000000",
              },
              "& .MuiInputLabel-root": {
                color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiFormControl-root > .MuiBox-root": {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />

          <UniversalTextField
            value={filesInPost}
            label={isVideoJob(jobData) ? "Edited" : "Files In Post"}
            disabled={itemHidden}
            onCallback={handleFilesInPostChange}
            adminOnly={false}
            sx={{
              marginTop: 0,
              width: isMobile ? "47%" : "100%",
              "& .MuiInputBase-input": {
                color: theme === "dark" ? "#ffffff" : "#000000",
              },
              "& .MuiInputLabel-root": {
                color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiFormControl-root > .MuiBox-root": {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />

          <UniversalTextField
            value={quantitySupplied}
            label="Supplied"
            disabled={itemHidden}
            onCallback={handleQuantitySuppliedChange}
            adminOnly={false}
            sx={{
              marginTop: 0,
              width: isMobile ? "47%" : "100%",
              backgroundColor: quantitySupplied
                ? theme === "dark"
                  ? "rgba(120, 175, 200, 0.3)"
                  : "rgba(120, 175, 200, 0.3)"
                : "",
              borderRadius: "8px",
              "& .MuiInputBase-input": {
                color: theme === "dark" ? "#ffffff" : "#000000",
              },
              "& .MuiInputLabel-root": {
                color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiFormControl-root > .MuiBox-root": {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />

          {!isVideoJob(jobData) && (
            <UniversalTextField
              value={expectedPerHour ? expectedPerHour : "---"}
              label="Expected Per Hour"
              disabled={true}
              adminOnly={false}
              sx={{
                marginTop: 0,
                width: isMobile ? "47%" : "100%",
                "& .MuiInputBase-input": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiInputLabel-root": {
                  color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiFormControl-root > .MuiBox-root": {
                  backgroundColor: "transparent !important",
                  boxShadow: "none !important",
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export default BreakdownItemV2;